import { io } from "socket.io-client";
import toastService from "./toastService";

class NetworkService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.eventListeners = new Map();
    this.playerId = null;
    this.roomCode = null;
    this.intentionalDisconnect = false; // Track intentional disconnections
  }

  // Connect to the server
  connect(serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:8080") {
    try {
      this.socket = io(serverUrl, {
        autoConnect: true,
        reconnection: false,
        timeout: 20000,
        transports: process.env.NODE_ENV === 'production' ? ['websocket'] : ['polling', 'websocket'],
      });

      this.setupEventListeners();
      return true;
    } catch (error) {
      return false;
    }
  }

  // Setup Socket.IO event listeners
  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.intentionalDisconnect = false; // Reset on successful connection
      this.emit("connection_status", { connected: true });
    });

    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;
      this.emit("connection_status", { connected: false, reason });

      // Only show disconnection toast if it was NOT intentional
      if (!this.intentionalDisconnect) {
        toastService.show("Disconnected from Server", "Please create or join a room again", "error", {
          position: "bottom-right",
          autoClose: false
        });
      }
      // Reset the flag after handling
      this.intentionalDisconnect = false;
    });

    this.socket.on("connect_error", (error) => {
      this.reconnectAttempts++;
      this.emit("connection_error", {
        error,
        attempts: this.reconnectAttempts,
      });
    });

    // Room-related events
    this.socket.on("player_joined", (data) => {
      this.emit("player_joined", data);
    });

    this.socket.on("player_left", (data) => {
      this.emit("player_left", data);
    });

    this.socket.on("player_ready_changed", (data) => {
      this.emit("player_ready_changed", data);
    });

    this.socket.on("game_started", (data) => {
      this.emit("game_started", data);
    });

    this.socket.on("game_event", (data) => {
      this.emit("game_event", data);
    });

    // Listen for game settings updates
    this.socket.on("game_settings_updated", (data) => {
      this.emit("game_settings_updated", data);
    });

    // Listen for player disconnections during game
    this.socket.on("player_disconnected_ingame", (data) => {
      this.emit("player_disconnected_ingame", data);
    });

    // Listen for game ended events
    this.socket.on("game_ended", (data) => {
      this.emit("game_ended", data);
    });

    // Listen for room state updates (for return to lobby)
    this.socket.on("room_state_updated", (data) => {
      this.emit("room_state_updated", data);
    });
  }

  // Create a new room
  async createRoom(playerName, theme = "theme-1", boardSkin = null) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error("Not connected to server"));
        return;
      }

      this.socket.emit("create_room", { playerName, theme, boardSkin }, (response) => {
        if (response.success) {
          this.playerId = response.playerId;
          this.roomCode = response.room.code;
          resolve(response);
        } else {
          reject(new Error(response.error));
        }
      });
    });
  }

  // Join an existing room
  async joinRoom(roomCode, playerName, theme = "theme-1", boardSkin = null) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error("Not connected to server"));
        return;
      }

      this.socket.emit(
        "join_room",
        { roomCode, playerName, theme, boardSkin },
        (response) => {
          if (response.success) {
            this.playerId = response.playerId;
            this.roomCode = response.room.code;
            resolve(response);
          } else {
            reject(new Error(response.error));
          }
        }
      );
    });
  }

  // Toggle player ready status
  async toggleReady(isReady) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected || !this.playerId) {
        reject(new Error("Not connected or no player ID"));
        return;
      }

      this.socket.emit(
        "toggle_ready",
        { playerId: this.playerId, isReady },
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.error));
          }
        }
      );
    });
  }

  // Start game (host only)
  async startGame() {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected || !this.playerId) {
        reject(new Error("Not connected or no player ID"));
        return;
      }

      this.socket.emit(
        "start_game",
        { playerId: this.playerId },
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.error));
          }
        }
      );
    });
  }

  // Update game settings (host only)
  async updateGameSettings(playerId, gameSettings) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error("Not connected"));
        return;
      }

      this.socket.emit(
        "update_game_settings",
        { playerId, gameSettings },
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(
              new Error(response.error || "Failed to update game settings")
            );
          }
        }
      );
    });
  }

  // Return to lobby (sync room state)
  async returnToLobby() {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected || !this.playerId) {
        reject(new Error("Not connected or no player ID"));
        return;
      }

      this.socket.emit(
        "return_to_lobby",
        { playerId: this.playerId },
        (response) => {
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.error || "Failed to return to lobby"));
          }
        }
      );
    });
  }

  // Send game event
  async sendGameEvent(eventType, eventData) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected || !this.playerId) {
        reject(new Error("Not connected or no player ID"));
        return;
      }

      this.socket.emit(
        "game_event",
        {
          playerId: this.playerId,
          eventType,
          eventData,
        },
        (response) => {
          if (response && response.success) {
            resolve(response);
          } else {
            // Handle validation errors with details
            let errorMessage = "Unknown error";
            let errorDetails = "";

            try {
              // Safely extract error message
              if (response && typeof response === 'object') {
                if (typeof response.error === 'string') {
                  errorMessage = response.error;
                } else if (response.error) {
                  // If error is an object, try to extract message safely
                  errorMessage = response.error.message || "Validation error";
                }

                // Extract validation details if present
                if (Array.isArray(response.details)) {
                  errorDetails = response.details.map(d => `${d.field}: ${d.message}`).join(", ");
                }
              } else {
                errorMessage = "Invalid response from server";
              }
            } catch (e) {
              errorMessage = "Error parsing server response";
            }

            const fullError = errorDetails ? `${errorMessage} (${errorDetails})` : errorMessage;
            reject(new Error(fullError));
          }
        }
      );
    });
  }

  // Leave current room
  leaveRoom() {
    if (this.socket && this.isConnected) {
      this.intentionalDisconnect = true; // Mark as intentional disconnect
      this.socket.disconnect();
      this.playerId = null;
      this.roomCode = null;
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          // Error in event listener handled silently
        }
      });
    }
  }

  // Cleanup
  disconnect() {
    if (this.socket) {
      this.intentionalDisconnect = true; // Mark as intentional disconnect
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.playerId = null;
    this.roomCode = null;
    this.eventListeners.clear();
  }

  // Getters
  getPlayerId() {
    return this.playerId;
  }

  getRoomCode() {
    return this.roomCode;
  }

  getConnectionStatus() {
    return this.isConnected;
  }
}

// Create a singleton instance
const networkService = new NetworkService();

export default networkService;
